import React from 'react';
import { motion, Variants } from 'framer-motion';
import './StatCard.css';
import SkeletonLoader from './SkeletonLoader';
import { formatCount, formatPkr } from 'utils/currency';

interface StatCardProps {
  title: string;
  value: string | number;
  icon: string;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  gradient?: string;
  color?: string;
  loading?: boolean;
  onClick?: () => void;
  suffix?: string;
  formatAsCurrency?: boolean;
}

/* =========================
   ANIMATION VARIANTS
========================= */

const containerVariants: Variants = {
  hidden: { opacity: 0, y: 20 },

  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.16, 1, 0.3, 1], // ✅ modern smooth easing (no TS error)
    },
  },

  hover: {
    y: -8,
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
    transition: {
      duration: 0.3,
      ease: [0.16, 1, 0.3, 1],
    },
  },
};

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  icon,
  trend,
  gradient = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color = '#667eea',
  loading = false,
  onClick,
  suffix = '',
  formatAsCurrency = false,
}) => {
  const formatNumber = (num: string | number): string => {
    const n = typeof num === 'string' ? parseFloat(num) : num;
    if (Number.isNaN(n)) return String(num);
    if (formatAsCurrency) return formatPkr(n);
    return formatCount(n);
  };
  /* =========================
     LOADING STATE (SHIMMER)
  ========================= */

  if (loading) {
    return (
      <div className="stat-card stat-card-loading">
        <div className="stat-card-header">
          <SkeletonLoader type="avatar" />
          <SkeletonLoader type="text" width="60px" height="14px" />
        </div>

        <SkeletonLoader type="text" width="80px" height="28px" />

        <SkeletonLoader type="text" width="100px" height="12px" />
      </div>
    );
  }

  /* =========================
     MAIN CARD
  ========================= */

  return (
    <motion.div
      className="stat-card"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      whileHover="hover"
      onClick={onClick}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
    >
      {/* Gradient Background */}
      <div className="stat-card-background" style={{ background: gradient }} />

      {/* Header */}
      <div className="stat-card-header">
        <div className="stat-icon" style={{ backgroundColor: color }}>
          <i className={`bx ${icon}`} />
        </div>
        <p className="stat-title">{title}</p>
      </div>

      {/* Content */}
      <div className="stat-card-content">
        <div className="stat-value-container">
          <h2 className="stat-value">
            {formatNumber(value)}
            {suffix && <span className="stat-suffix">{suffix}</span>}
          </h2>
        </div>
      </div>

      {/* Footer */}
      <div className="stat-card-footer">
        {trend && (
          <div className="stat-footer-inline">
            <div className={`stat-trend ${trend.isPositive ? 'positive' : 'negative'}`}>
              <i className={`bx ${trend.isPositive ? 'bx-up-arrow-alt' : 'bx-down-arrow-alt'}`} />
              <span>{Math.abs(trend.value)}%</span>
            </div>

            <span className="stat-period">vs last period</span>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default StatCard;
